import { debounce, isEqual } from 'lodash';

// ---------------------------------------------------------------------------
// Generic debounced whole-document write-back, extracted from `layerConfigSync.ts` (user) and
// `orgLayerConfigSync.ts` (org) — both duplicated the same write machine: a baseline of the
// last-persisted document, a structural dirty diff, a trailing debounce coalescing a burst of
// edits into one PUT, lifecycle flushes, and clear-on-success / retain-on-failure.
//
// Writes go through a SINGLE serialized channel: at most one PUT is in flight at a time
// (`inFlight`), and the next (debounced) PUT is dispatched only after the prior one has resolved.
// Because writes never overlap they cannot reach the server out of order, so the server's
// last-write-wins upsert is correct with no version/sequence token.
//
// The store-specific differences (subscription topology, the user-only `pausePersistence` guard,
// the org-only `saveError` bookkeeping) are injected via `opts` rather than hard-coded here — see
// `layerConfigSync.ts` / `orgLayerConfigSync.ts` for the adapters.
// ---------------------------------------------------------------------------

/** Trailing debounce window: a burst of edits coalesces into one PUT. */
const DEBOUNCE_MS = 1000;
/** A long continuous burst still flushes at least this often. */
const MAX_WAIT_MS = 5000;

export interface DocumentSyncController {
  start(): void;
  stop(): void;
  isDirty(): boolean;
  scheduleFlush(): void;
  flush(): Promise<void>;
  flushNow(): void;
}

export interface DocumentSyncControllerOptions<TState, TDoc> {
  /** Whether persistence is armed (the no-overwrite invariant is decided by the store, honored here). */
  canPersist: () => boolean;
  /** Current in-memory state, or `null` when there is nothing to serialize. */
  getState: () => TState | null;
  /** Serialize state into the canonical document shape sent to the server. */
  toDocument: (state: TState) => TDoc;
  /** PUT the whole document for an org. */
  put: (organizationId: string, doc: TDoc) => Promise<void>;
  /** Wire store-specific change sources; return a single teardown for all of them. */
  subscribe: (onChange: () => void) => () => void;
  /** Wire the store's hydrate lifecycle slot; return an unsubscribe. */
  onHydrate: (fn: (doc: TDoc, organizationId: string | null) => void) => () => void;
  /** Extra skip condition checked alongside `canPersist`. `true` ⇒ skip. Default: never skip. */
  extraGuard?: () => boolean;
  /** Called after a successful PUT (under the still-current-org guard). Default: no-op. */
  onPersistSuccess?: () => void;
  /** Called after a failed PUT (under the still-current-org guard). Default: no-op. */
  onPersistError?: () => void;
  debounceMs?: number;
  maxWaitMs?: number;
}

export function createDocumentSyncController<TState, TDoc>(
  opts: DocumentSyncControllerOptions<TState, TDoc>,
): DocumentSyncController {
  const extraGuard = opts.extraGuard ?? (() => false);
  const onPersistSuccess = opts.onPersistSuccess ?? (() => {});
  const onPersistError = opts.onPersistError ?? (() => {});

  /**
   * The document as the server last confirmed it — set on hydrate and after every
   * successful PUT. Dirty = current serialized state differs structurally from this.
   * `null` until the first hydrate; with no baseline nothing is ever written.
   */
  let lastPersisted: TDoc | null = null;
  /** Org of the hydrated document; PUTs are addressed to it. `null` disables writes. */
  let organizationId: string | null = null;
  /** One PUT in flight at a time; an edit during a flight re-arms the debounce. */
  let inFlight = false;
  /**
   * A synchronous (route-change) flush was requested via `flushNow` while a PUT was in flight.
   * The in-flight `flush()`'s completion path runs one immediate (non-debounced) flush
   * to write the edits that arrived after dispatch, instead of leaving only a debounced flush
   * that `stop()` could cancel on unmount.
   */
  let pendingSyncFlush = false;
  let started = false;
  let unsubscribers: Array<() => void> = [];

  const debouncedFlush = debounce(
    () => {
      void flush();
    },
    opts.debounceMs ?? DEBOUNCE_MS,
    { trailing: true, maxWait: opts.maxWaitMs ?? MAX_WAIT_MS },
  );

  /** Current state as a canonical document, or `null` when there is nothing writable. */
  function serializeCurrent(): TDoc | null {
    if (!opts.canPersist() || !lastPersisted || !organizationId) return null;
    const state = opts.getState();
    return state ? opts.toDocument(state) : null;
  }

  /**
   * Baseline reset on hydrate: the just-hydrated document IS the persisted truth — a
   * fresh org/document never inherits the previous one's pending write. Any write still in
   * flight from the previous org is dropped on resolve by `flush`'s org guard.
   */
  function onHydrated(document: TDoc, orgId: string | null) {
    debouncedFlush.cancel();
    lastPersisted = document;
    organizationId = orgId;
  }

  /**
   * Arm/extend the trailing debounce. No-op while persistence is not armed (`canPersist` false)
   * or while `extraGuard` reports true (user: CG-752 `pausePersistence`).
   */
  function scheduleFlush() {
    if (!opts.canPersist() || extraGuard()) return;
    debouncedFlush();
  }

  /**
   * The debounced write: serialize current state at fire time (never a snapshot from
   * schedule time), PUT the whole document, and on success move the baseline to what was
   * sent — then re-diff, because edits may have arrived during the flight, and reschedule
   * if so. On failure the baseline (and thus dirty) is retained; the NEXT flush trigger
   * (change or lifecycle) retries — no hot retry loop against a failing server.
   *
   * Only one PUT is ever in flight, so writes reach the server in dispatch order and the
   * server's last-write-wins upsert needs no ordering token.
   */
  async function flush(): Promise<void> {
    if (inFlight || !opts.canPersist() || !organizationId || extraGuard()) return;
    const document = serializeCurrent();
    if (!document || isEqual(document, lastPersisted)) return;

    const dispatchedOrgId = organizationId;
    inFlight = true;
    let succeeded = false;
    try {
      await opts.put(dispatchedOrgId, document);
      // Move the baseline only if we are still on the org we dispatched for. A stale write
      // resolving after an org switch (its org no longer current) is dropped, so it cannot
      // regress the freshly-hydrated baseline of the new org.
      if (dispatchedOrgId === organizationId) {
        lastPersisted = document;
        onPersistSuccess();
        succeeded = true;
      }
    } catch {
      // Retain dirty (baseline unchanged); retried on the next flush trigger.
      if (dispatchedOrgId === organizationId) onPersistError();
    } finally {
      inFlight = false;
    }
    // A route-change flush requested mid-flight runs now, immediately, so the unmount
    // path can't cancel it — even on failure, where it serves as the single requested retry.
    if (pendingSyncFlush) {
      pendingSyncFlush = false;
      void flush();
      return;
    }
    // Re-arm also when the flight resolved for a superseded org (`succeeded` forced false by
    // the org guard): a debounce that fired during this flight was consumed by the inFlight
    // early-return, and it may have carried the NEW org's first dirty edit — without the
    // re-arm that edit sits unsynced until the next change or lifecycle flush.
    const staleOrg = dispatchedOrgId !== organizationId;
    if ((succeeded || staleOrg) && isDirty()) scheduleFlush();
  }

  /**
   * Immediate flush for lifecycle boundaries (route change, tab hidden, unload): cancel the
   * pending debounce and write now. Uses the same normal PUT as the debounced path; on a
   * true page teardown the request may be cancelled, so teardown persistence is best-effort.
   */
  function flushNow() {
    debouncedFlush.cancel();
    if (extraGuard()) return;
    // A PUT is already in flight: defer to its completion path rather than dropping the
    // synchronous request — `flush()` would early-return on `inFlight` and the post-flight
    // reschedule only arms a debounced flush, which `stop()` can cancel on unmount.
    if (inFlight) {
      pendingSyncFlush = true;
      return;
    }
    void flush();
  }

  /**
   * Whether the in-memory state differs from the last-persisted document. Self-
   * correcting: a toggle-then-untoggle back to the baseline is NOT dirty. Always false while
   * persistence is not armed (`canPersist` false) or before the first hydrate.
   */
  function isDirty(): boolean {
    const document = serializeCurrent();
    return document !== null && !isEqual(document, lastPersisted);
  }

  /**
   * Wire the controller into the store: the injected subscription topology plus the hydrate
   * lifecycle slot. Idempotent — the React hook may remount.
   */
  function start() {
    if (started) return;
    started = true;
    unsubscribers = [opts.subscribe(() => scheduleFlush()), opts.onHydrate((doc, orgId) => onHydrated(doc, orgId))];
  }

  /** Tear down all subscriptions and cancel any pending flush. */
  function stop() {
    if (!started) return;
    started = false;
    unsubscribers.forEach((unsubscribe) => {
      unsubscribe();
    });
    unsubscribers = [];
    debouncedFlush.cancel();
  }

  return { start, stop, isDirty, scheduleFlush, flush, flushNow };
}
